import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaUsers, FaClock, FaStar, FaChalkboardTeacher, FaExclamationTriangle } from 'react-icons/fa';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import { isToday } from '../../utils/helpers';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ArcElement, Tooltip, Legend);

/**
 * Dashboard component for mentors showing stats and upcoming sessions
 */
const MentorDashboard = () => {
  const [stats, setStats] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const { showError } = useToast();
  
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setErrors({});
        
        const response = await api.get('/mentors/dashboard');
        const data = response.data.data || {};
        
        // Track missing data
        const missingData = {};
        
        if (data.stats) {
          setStats(data.stats);
        } else {
          missingData.stats = true;
        }
        
        if (data.upcomingSessions && data.upcomingSessions.length > 0) {
          setUpcomingSessions(data.upcomingSessions);
        } else {
          // Empty array is valid (no upcoming sessions)
          // Only mark as missing if the data is actually missing
          if (!data.upcomingSessions) {
            missingData.sessions = true;
          }
        }
        
        // Set errors if any data is missing
        if (Object.keys(missingData).length > 0) {
          setErrors({
            type: 'incomplete',
            missingData,
            message: 'Some dashboard data is unavailable.'
          });
        }
        
      } catch (error) {
        console.error('Error fetching mentor dashboard data:', error);
        showError('Failed to load dashboard data');
        
        if (error.response) {
          setErrors({ 
            type: 'serverError', 
            message: 'Server error: ' + (error.response.data?.message || 'Unable to retrieve data.'),
            allMissing: true
          });
        } else if (error.request) {
          setErrors({ 
            type: 'connectionError', 
            message: 'Cannot connect to the server. Please check your network connection.',
            allMissing: true
          });
        } else {
          setErrors({ 
            type: 'unknownError', 
            message: 'An unexpected error occurred while loading data.',
            allMissing: true
          });
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [showError]);
  
  if (loading) {
    return (
      <div className="h-[600px] flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Render complete error state only if we have no data at all
  if (errors.allMissing) {
    return (
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">Mentor Dashboard</h1>
        
        <div className="bg-white rounded-lg shadow-soft p-8 text-center">
          <FaExclamationTriangle className="text-yellow-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {errors.type === 'connectionError' ? 'Connection Error' : 'Data Unavailable'}
          </h2>
          <p className="text-gray-600 mb-4">{errors.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary inline-flex items-center"
          >
            <FaCalendarAlt className="mr-2" /> Retry Loading
          </button>
        </div>
      </div>
    );
  }
  
  // Prepare chart data based on available stats
  const sessionActivityData = stats ? {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Sessions',
        data: stats.sessionsByMonth || [0, 0, 0, 0, 0, 0, 0],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.3,
      },
    ],
  } : null;
  
  const sessionActivityOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Monthly Sessions',
        font: {
          size: 14,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };
  
  // Rating distribution chart data
  const ratingData = stats ? {
    labels: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
    datasets: [
      {
        data: stats.ratingDistribution || [0, 0, 0, 0, 0],
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(255, 205, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(54, 162, 235, 0.7)',
        ],
        borderWidth: 1,
      },
    ],
  } : null;
  
  // Format date-time
  const formatDateTime = (dateString) => {
    const options = { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Mentor Dashboard</h1>
      
      {/* Global error message */}
      {errors.type && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{errors.message}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Cards Section */}
      <div className="mb-6">
        {errors.missingData?.stats && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
            <p className="text-sm text-yellow-700 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-2" />
              Mentor statistics are unavailable
            </p>
          </div>
        )}
        
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-blue-100 p-3 mr-4">
                <FaUsers className="text-blue-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-2xl font-bold">{stats.totalStudents}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-green-100 p-3 mr-4">
                <FaCalendarAlt className="text-green-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Sessions This Month</p>
                <p className="text-2xl font-bold">{stats.sessionsThisMonth}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-purple-100 p-3 mr-4">
                <FaClock className="text-purple-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Hours This Month</p>
                <p className="text-2xl font-bold">{stats.hoursThisMonth}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-yellow-100 p-3 mr-4">
                <FaStar className="text-yellow-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Average Rating</p>
                <p className="text-2xl font-bold">{stats.averageRating.toFixed(1)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold mb-4">Session Activity</h2>
          
          {errors.missingData?.stats && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-center">
                <FaExclamationTriangle className="text-yellow-500 mr-2" />
                Session activity data is unavailable
              </p>
            </div>
          )}
          
          {sessionActivityData ? (
            <Line options={sessionActivityOptions} data={sessionActivityData} height={100} />
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-2" />
              <p className="text-gray-500">Session activity data unavailable</p>
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold mb-4 text-center">Rating Distribution</h2>
          
          {errors.missingData?.stats && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-center">
                <FaExclamationTriangle className="text-yellow-500 mr-2" />
                Rating distribution data is unavailable
              </p>
            </div>
          )}
          
          {ratingData ? (
            <div className="h-64 flex justify-center">
              <Doughnut 
                data={ratingData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-2" />
              <p className="text-gray-500">Rating distribution data unavailable</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Upcoming Sessions */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Upcoming Sessions</h2>
          <Link to="/mentor/sessions" className="text-primary hover:text-primary-600 text-sm font-medium">
            View All
          </Link>
        </div>
        
        {errors.missingData?.sessions && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
            <p className="text-sm text-yellow-700 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-2" />
              Upcoming sessions data is unavailable
            </p>
          </div>
        )}
        
        {errors.missingData?.sessions ? (
          <div className="text-center py-8">
            <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
            <p className="text-gray-500">Session data unavailable</p>
          </div>
        ) : upcomingSessions.length === 0 ? (
          <div className="text-center py-8">
            <FaChalkboardTeacher className="mx-auto text-gray-300 text-4xl mb-3" />
            <p className="text-gray-500">No upcoming sessions scheduled</p>
            <Link 
              to="/mentor/sessions" 
              className="mt-3 inline-block px-4 py-2 bg-primary text-white rounded-md text-sm font-medium"
            >
              Manage Sessions
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {upcomingSessions.map((session) => (
                  <tr key={session._id} className={isToday(session.scheduledDate) ? 'bg-blue-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center mr-3">
                          {session.child.firstName.charAt(0)}
                          {session.child.lastName.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {session.child.firstName} {session.child.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {session.parent.firstName} {session.parent.lastName} (Parent)
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${isToday(session.scheduledDate) ? 'font-semibold text-primary' : 'text-gray-900'}`}>
                        {formatDateTime(session.scheduledDate)}
                      </div>
                      {isToday(session.scheduledDate) && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Today
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {session.sessionType === 'video_call' 
                          ? 'Video Call' 
                          : session.sessionType === 'in_person'
                            ? 'In Person'
                            : session.sessionType === 'phone_call'
                              ? 'Phone Call'
                              : 'Chat'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.duration} minutes
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link 
                        to={`/mentor/sessions/${session._id}`} 
                        className="text-primary hover:text-primary-600"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MentorDashboard;
